import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTheme } from "@mui/material";
import { tokens } from "../../../theme";
import { Heading, Text } from "../../../components/DesignSystem/components/Typography";
import { Input } from "../../../components/DesignSystem/components/Input";
import { Button as DSButton } from "../../../components/DesignSystem/components/Button";
import { Chip } from "../../../components/DesignSystem/components/Chip";
import { CustomDatePicker } from "./CustomDatePicker";
import { ArrowLeft, ArrowRight, X, User, Users, Globe2, ScanFace, HeartHandshake, Sparkles, ChevronDown } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import * as userActions from "../../../actions/user";
import * as userPreferencesActions from "../../../actions/userPreferences";
import { useCookies } from "../../../hooks/useCookies";
import { useAppContext } from "../../../Context/UseContext";
import dayjs from "dayjs";
import { Spinner } from "react-bootstrap";
import log from "../../../utils/log";
import aboutBg from "../../../assets/images/st5.png";

// Validation functions
const validateName = (name) => {
  if (!name) return "Name is required.";
  if (name.length < 2) return "Name must be at least two letters long";
  if (!/^[a-zA-Z\s]+$/.test(name)) return "Please enter a valid name (letters only)";
  return "";
};

const validateAge = (birthday) => {
  if (!birthday) return "Please select your birth date.";
  const age = dayjs().diff(dayjs(birthday), "years");
  if (age <= 18) return "You must be over 19 to join MTD.";
  return "";
};

export const AboutYourself = () => {
    const navigate = useNavigate();
    
    // 1. Theme Configuration Hook
    const theme = useTheme();
    const mode = theme.palette.mode;
    
    // 2. FOUC Tracker State
    const [isTailwindLoaded, setIsTailwindLoaded] = useState(!!window.tailwind);

    // 3. Centralized FOUC Prevention & Engine Mount
    useEffect(() => {
        const applyConfig = () => {
            const modeColors = tokens(mode);
            if (window.tailwind) {
                window.tailwind.config = {
                    darkMode: "class",
                    theme: {
                        extend: {
                            colors: {
                                "brand": modeColors.greenAccent[500] || "#ff794d",
                                "background-light": mode === "dark" ? modeColors.primary[500] : "#f8f6f5",
                                "background-dark": modeColors.primary[500] || "#23140f",
                                "action-black": "#111111",
                            },
                            fontFamily: {
                                "display": ["Inter", "sans-serif"],
                                "body": ['"Source Sans Pro"', "sans-serif"],
                            },
                        },
                    },
                };
            }
        };

        if (window.tailwind) {
            applyConfig();
            setIsTailwindLoaded(true);
        } else {
            const script = document.createElement("script");
            script.src = "https://cdn.tailwindcss.com?plugins=forms";
            script.onload = () => {
                applyConfig();
                setIsTailwindLoaded(true);
            };
            document.head.appendChild(script);
        }
    }, [mode]);

    // Handle button selections state
    const [gender, setGender] = useState('Woman');
    const [preference, setPreference] = useState('Everyone');

    const isFetched = useRef(false);
    const { getCookie } = useCookies();
    const token = getCookie("token");
    const { userDetails, setUserDetails } = useAppContext();
    const [errorMessage, setErrorMessage] = useState("");
    const [errorBirthday, setErrorBirthday] = useState("");
    const [havegenderError, setHavegenderError] = useState("");
    const [wantGenderError, setWantGenderError] = useState("");
    const [loading, setLoading] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [hasScrolled, setHasScrolled] = useState(false);

    const checkScrollPosition = useCallback((e) => {
        const { scrollTop } = e.target;
        // If they scroll down significantly (> 20px), hide the indicator permanently
        if (scrollTop > 20) {
            setHasScrolled(true);
        }
    }, []);

    // Also trigger check after data load to see if scrolling is even needed
    useEffect(() => {
        const container = document.getElementById("onboarding-scroll-container");
        if (container) {
            if (container.scrollHeight <= container.clientHeight) {
                setHasScrolled(true);
            }
        }
    }, [userDetails]);

    const fetchUserDetails = useCallback(async () => {
        try {
            setIsLoadingData(true);
            const data = await userActions.getNameDetails();
            setUserDetails(data);
        } catch (error) {
            console.error("Error fetching user details:", error);
        } finally {
            setIsLoadingData(false);
        }
    }, [setUserDetails]);

    const fetchGenderDetails = useCallback(async () => {
        try {
            const response = await userActions.getUserGender();
            const { gender, want_gender } = response;
            if (gender === 1) setGender("Man");
            else if (gender === 2) setGender("Woman");
            else if (gender === 3) setGender("Non-binary");

            if (want_gender === 1) setPreference("Men");
            else if (want_gender === 2) setPreference("Women");
            else if (want_gender === 3) setPreference("Everyone");
        } catch (error) {
            console.error("Error fetching user gender:", error);
        }
    }, []);

    useEffect(() => {
        if (isFetched.current) return;
        isFetched.current = true;
        if (!token) return;
        fetchUserDetails();
        fetchGenderDetails();
    }, [token, fetchUserDetails, fetchGenderDetails]);

    const handleNameChange = useCallback((e) => {
        setErrorMessage("");
        const value = e.target.value.replace(/[^a-zA-Z\s]/g, "");
        setUserDetails(prevDetails => ({
            ...prevDetails,
            first_name: value.charAt(0).toUpperCase() + value.slice(1).toLowerCase(),
        }));
    }, [setUserDetails]);

    const handleLastNameChange = useCallback((e) => {
        const value = e.target.value.replace(/[^a-zA-Z\s]/g, "");
        setUserDetails(prevDetails => ({
            ...prevDetails,
            last_name: value.charAt(0).toUpperCase() + value.slice(1).toLowerCase(),
        }));
    }, [setUserDetails]);

    const handleBirthdayChange = useCallback((e) => {
        const value = e.target.value;
        setErrorBirthday("");
        setUserDetails(prevDetails => ({
            ...prevDetails,
            birthday: value,
        }));
    }, [setUserDetails]);

    const updateAgePreferences = useCallback(async (calculatedAge) => {
        try {
            let age_from = Math.max(calculatedAge - 5, 18);
            let age_to = calculatedAge + 5;
            await userPreferencesActions.updateAge(
                JSON.stringify({ age_from, age_to })
            );
        } catch (error) {
            console.error("Error updating preferences:", error);
            throw new Error("Failed to update preferences");
        }
    }, []);

    const updateGenderPreferences = async (wantGender) => {
        try {
            await userPreferencesActions.updateGender(
                JSON.stringify({ value: wantGender })
            );
        } catch (error) {
            console.error(error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (loading) return;

        setErrorMessage("");
        setErrorBirthday("");
        setHavegenderError("");
        setWantGenderError("");

        const nameError = validateName(userDetails?.first_name);
        if (nameError) {
            setErrorMessage(nameError);
            return;
        }

        const birthdayError = validateAge(userDetails?.birthday);
        if (birthdayError) {
            setErrorBirthday(birthdayError);
            return;
        }

        if (!gender) {
            setHavegenderError("*Please make a selection.");
            return;
        }
        if (!preference) {
            setWantGenderError("*Please make a selection.");
            return;
        }

        const havegenderId = gender === "Woman" ? 2 : gender === "Man" ? 1 : 3;
        const wantGenderId = preference === "Women" ? 2 : preference === "Men" ? 1 : 3;

        setLoading(true);
        // Navigate instantly for smooth UX
        navigate("/located");
        
        try {
            // Update Basic Details (Name, Birthday, Age Pref)
            const calculatedAge = dayjs().diff(dayjs(userDetails.birthday), "years");
            
            Promise.all([
                updateAgePreferences(calculatedAge),
                userActions.updateNameDetails(JSON.stringify(userDetails)),
                updateGenderPreferences(wantGenderId),
                userActions.updateUserGender(
                    JSON.stringify({
                        gender: havegenderId,
                        want_gender: wantGenderId,
                    })
                )
            ]).catch((error) => {
                console.error("Error updating profile in background:", error);
            });

        } catch (error) {
            console.error("Error executing update logic:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div 
            className={`${mode === "dark" ? "dark" : ""} bg-background-light dark:bg-background-dark font-display text-slate-900 dark:text-slate-100 antialiased h-[100dvh] overflow-hidden w-full`}
            style={{ opacity: isTailwindLoaded ? 1 : 0, transition: 'opacity 0.1s ease-out' }}
        >
            <div className="flex h-full w-full flex-col lg:flex-row overflow-hidden">
                
                {/* ---------- LEFT SIDE: HERO/VISUAL ---------- */}
                <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden h-full">
                    <div className="absolute inset-0 bg-brand/10 mix-blend-multiply z-10"></div>
                    <div className="absolute inset-0 bg-cover bg-center bg-no-repeat w-full h-full" style={{ backgroundImage: `url(${aboutBg})` }}></div>
                    
                    {/* Floating Branding Header */}
                    <div className="absolute bottom-12 left-12 z-20 max-w-md">
                        <Heading variant="h2" className="login-hero-title mb-[var(--ds-space-4)]">Find connections that matter.</Heading>
                        <Text as="p" className="ds-text-hero-subtitle">Our community is built on authenticity and shared values.</Text>
                    </div>
                </div>
                
                {/* ---------- RIGHT SIDE: CONTENT / FORM ---------- */}
                <div className="w-full lg:w-1/2 flex flex-col bg-white dark:bg-background-dark h-full overflow-hidden relative">
                    
                    {/* Header/Nav */}
                    <header className="flex-none bg-background-light/95 dark:bg-background-dark/95 backdrop-blur-md px-[var(--ds-space-6)] py-[var(--ds-space-4)] flex items-center justify-between border-b border-brand/10 z-30 relative">
                        <button type="button" onClick={() => navigate(-1)} className="flex items-center justify-center h-10 w-10 shrink-0 rounded-full bg-brand/10 text-action-black dark:text-white hover:bg-brand/20 transition-colors" aria-label="Go back">
                            <ArrowLeft className="shrink-0" size={24} />
                        </button>
                        <div className="flex items-center gap-[var(--ds-space-2)]">
                            <div className="h-2 w-24 bg-brand/20 rounded-full overflow-hidden">
                                <div className="h-full bg-brand" style={{ width: '17%' }}></div>
                            </div>
                            <span className="text-xs font-bold uppercase tracking-wider text-brand">Step 1 of 6</span>
                        </div>
                        <button type="button" className="flex items-center justify-center h-10 w-10 shrink-0 rounded-full bg-brand/10 text-action-black dark:text-white hover:bg-brand/20 transition-colors" aria-label="Close">
                            <X className="shrink-0" size={24} />
                        </button>
                    </header>

                    <div 
                        id="onboarding-scroll-container"
                        className="flex-1 overflow-y-auto custom-scrollbar flex flex-col relative"
                        onScroll={checkScrollPosition}
                    >
                        <main className="flex-1 px-[var(--ds-space-6)] py-[var(--ds-space-6)] lg:px-[var(--ds-space-12)] lg:py-[var(--ds-space-12)] max-w-2xl mx-auto w-full flex flex-col">
                            <div className="mb-[var(--ds-space-8)]">
                            <Heading variant="h1" className="ds-heading-page-title text-slate-900 dark:text-slate-100 mb-[var(--ds-space-2)]">
                                Tell us about yourself
                            </Heading>
                            <Text as="p" className="ds-text-page-subtitle text-slate-500 dark:text-slate-400">Help us get to know the real you so we can find better matches.</Text>
                        </div>

                        <form className="flex flex-col flex-1 gap-[var(--ds-space-8)]" onSubmit={handleSubmit}>
                            {/* Basic Info Section */}
                            <div className="flex flex-col md:flex-row gap-[var(--ds-space-6)]">
                                <div className="flex-1 flex flex-col gap-[var(--ds-space-2)]">
                                    <Input 
                                        label={<React.Fragment>First Name <span className="text-red-500">*</span></React.Fragment>}
                                        labelClassName="ds-label-form text-slate-400 ml-1 mb-1"
                                        placeholder="e.g. Julian" 
                                        required 
                                        value={userDetails?.first_name || ""}
                                        onChange={handleNameChange}
                                        className="w-full"
                                        style={{ 
                                            backgroundColor: mode === "dark" ? "rgba(30, 41, 59, 0.5)" : "#f8fafc", 
                                            borderColor: errorMessage ? "#ef4444" : (mode === "dark" ? "#334155" : "#e2e8f0"), 
                                            color: mode === "dark" ? "#f1f5f9" : "#0f172a" 
                                        }}
                                        disabled={loading || isLoadingData}
                                    />
                                    {errorMessage && <span className="text-red-500 text-xs ml-1">{errorMessage}</span>}
                                </div>
                                <div className="flex-1 flex flex-col gap-[var(--ds-space-2)]">
                                    <Input 
                                        label={<React.Fragment>Last Name <span className="font-normal">(Optional)</span></React.Fragment>}
                                        labelClassName="ds-label-form text-slate-400 ml-1 mb-1"
                                        placeholder="e.g. Smith" 
                                        value={userDetails?.last_name || ""}
                                        onChange={handleLastNameChange}
                                        className="w-full"
                                        style={{ 
                                            backgroundColor: mode === "dark" ? "rgba(30, 41, 59, 0.5)" : "#f8fafc", 
                                            borderColor: mode === "dark" ? "#334155" : "#e2e8f0", 
                                            color: mode === "dark" ? "#f1f5f9" : "#0f172a" 
                                        }}
                                        disabled={loading || isLoadingData}
                                    />
                                </div>
                            </div>

                            <div className="flex flex-col gap-[var(--ds-space-2)]">
                                <div className="relative">
                                    <CustomDatePicker
                                        label={<React.Fragment>Birthday <span className="text-red-500">*</span></React.Fragment>}
                                        labelClassName="ds-label-form text-slate-400 ml-1 mb-1"
                                        value={userDetails?.birthday || ""}
                                        onChange={handleBirthdayChange}
                                        error={errorBirthday}
                                        mode={mode}
                                        disabled={loading || isLoadingData}
                                        maxDate={dayjs().format("YYYY-MM-DD")}
                                    />
                                </div>
                                {errorBirthday ? (
                                    <span className="text-red-500 text-xs ml-1">{errorBirthday}</span>
                                ) : (
                                    <Text as="p" className="ds-text-disclaimer text-slate-400 ml-1">
                                        {userDetails?.birthday 
                                            ? `Your age (${dayjs().diff(dayjs(userDetails.birthday), "years")}) will be visible, but your full birthday stays private.` 
                                            : "Your age will be visible, but your full birthday stays private."
                                        }
                                    </Text>
                                )}
                            </div>

                            {/* Identity Selection */}
                            <div className="flex flex-col gap-[var(--ds-space-4)] mt-[var(--ds-space-4)]">
                                <div className="flex items-center gap-1 pb-2">
                                    <Heading variant="h3" className="text-xl lg:text-2xl font-bold text-slate-800 dark:text-slate-200">
                                        I am a
                                    </Heading>
                                </div>
                                <div className="flex flex-wrap gap-3 w-full">
                                    {['Woman', 'Man', 'Non-binary'].map((type) => {
                                        return (
                                            <Chip 
                                                key={type}
                                                label={type}
                                                selected={gender === type}
                                                onClick={() => setGender(type)}
                                                className={`transition-all duration-300 ${gender === type ? 'ring-2 ring-brand/20 shadow-md scale-105' : 'hover:scale-105'}`}
                                            />
                                        )
                                    })}
                                </div>
                                {havegenderError && <span className="text-red-500 text-xs ml-1">{havegenderError}</span>}
                            </div>

                            {/* Preference Selection */}
                            <div className="flex flex-col gap-[var(--ds-space-4)] mt-[var(--ds-space-2)]">
                                <div className="flex items-center gap-1 pb-2">
                                    <Heading variant="h3" className="text-xl lg:text-2xl font-bold text-slate-800">
                                        I want to date
                                    </Heading>
                                </div>
                                <div className="flex flex-wrap gap-3 w-full">
                                    {['Women', 'Men', 'Everyone'].map((type) => {
                                        return (
                                            <Chip 
                                                key={type}
                                                label={type}
                                                selected={preference === type}
                                                onClick={() => setPreference(type)}
                                                className={`transition-all duration-300 ${preference === type ? 'ring-2 ring-brand/20 shadow-md scale-105' : 'hover:scale-105'}`}
                                            />
                                        )
                                    })}
                                </div>
                                {wantGenderError && <span className="text-red-500 text-xs ml-1">{wantGenderError}</span>}
                            </div>

                        </form>
                    </main>

                    {/* Footer Meta */}
                    <footer className="mt-auto p-[var(--ds-space-6)] text-center pb-12">
                        <Text as="p" className="ds-text-disclaimer text-slate-400">
                            By continuing, you agree to our <Link to="/Tnc" className="text-brand hover:underline font-medium">Terms of Service</Link> and <Link to="/PrivacyPolicy" className="text-brand hover:underline font-medium">Privacy Policy</Link>.
                        </Text>
                    </footer>
                    
                    {/* Dynamic Scroll Indicator */}
                    <div className={`absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-t from-white dark:from-background-dark to-transparent pointer-events-none transition-opacity duration-500 flex items-end justify-center pb-4 ${hasScrolled ? 'opacity-0' : 'opacity-100'}`}>
                        <div className="flex items-center gap-1.5 text-brand font-medium text-[11px] uppercase tracking-wider animate-bounce shadow-md bg-white/95 dark:bg-slate-800/95 px-4 py-2 mt-4 rounded-full backdrop-blur-md mb-2">
                            <span>Scroll to complete</span>
                            <ChevronDown size={14} />
                        </div>
                    </div>
                </div>

                {/* Fixed Action Button */}
                <div className="flex-none p-[var(--ds-space-6)] pb-[calc(var(--ds-space-6)+env(safe-area-inset-bottom,20px))] border-t border-slate-100 dark:border-slate-800/60 bg-white/95 dark:bg-background-dark/95 backdrop-blur-lg z-50 shadow-[0_-10px_40px_rgba(0,0,0,0.03)] relative">
                    <DSButton 
                        type="submit"
                        onClick={handleSubmit}
                        variant="cta"
                        className="w-full shadow-lg shadow-brand/20"
                        endIcon={!loading && <ArrowRight size={20} />}
                        disabled={loading}
                    >
                        {loading ? <Spinner animation="border" size="sm" /> : "Continue"}
                    </DSButton>
                </div>
            </div>
        </div>
            {/* Inject minimal scrollbar styles for this component */}
            <style dangerouslySetInnerHTML={{__html: `
                .custom-scrollbar::-webkit-scrollbar { width: 4px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #ff794d33; border-radius: 10px; }
            `}} />
        </div>
    );
};
